RivalIQ.module('views', function (views, App, Backbone, Marionette, $, _, d3) {

    'use strict';

    views.DualGraphBar = Marionette.CompositeView.extend({
        ui: {
            bar: '.x-dual-graph-bar-parts',
            labels: '.x-dual-graph-bar-labels',
            wrapper: '.x-dual-graph-bar-wrapper',
            primaryLabel: '.x-dual-graph-bar-labels-primary',
            secondaryLabel: '.x-dual-graph-bar-labels-secondary',
            title: '.x-dual-graph-bar-title',
            notAvailable: '.x-dual-graph-bar-not-available',
            partialDataWarning: '.x-dual-graph-partial-data-warning'
        },
        className: 'x-dual-graph-bar',
        negativeClassName: 'x-negative',
        template: '#dualGraphBarTemplate',
        allNegativeThreshold: 1,
        mixedPositiveThreshold: 0.5,
        mixedNegativeThreshold: 0.5,
        childView: views.DualGraphBarPart,
        childViewContainer: '.x-dual-graph-bar-parts',
        initialize: function (options) {
            const me = this;

            me.options = options;
            me.collection = new Backbone.Collection(me.model.get('values'));
        },
        childViewOptions: function (model, index) {
            const me = this;
            const collection = me.collection;

            return {
                // we need to calculate the sum of all the values so that
                // in the bar part we can determine its percentage (part.value / sum);
                // which is used as the width
                sum: collection.reduce(function (memo, model) {
                    return memo + model.get('value');
                }, 0),
                color: me.getColor(model.toJSON(), index),
                tooltipPlacement: (me.getOption('barIndex') === 0 ? 'top' : 'bottom')
            };
        },
        getColor: function (data, index) {
            const me = this;
            const color = me.getOption('color');

            if (color) {
                return color()(data, index);
            }
            return null;
        },
        onRender: function () {
            const me = this;
            const model = me.model;

            const cls = model.get('class');

            if (typeof cls === 'function') {
                me.$el.addClass(cls());
            } else if (typeof cls === 'string') {
                me.$el.addClass(cls);
            }

            const title = model.get('title');
            const titleHtml = model.get('titleHtml');

            if (titleHtml) {
                me.ui.title.html(titleHtml);
            } else if (title) {
                if (_.isFunction(title.render)) {
                    title.render().$el.appendTo(me.ui.title);
                } else if (_.isFunction(title)) {
                    me.ui.title.text(title());
                } else {
                    me.ui.title.text(title);
                }
            }

            const titleTooltip = model.get('titleTooltip');
            if (titleTooltip) {
                RivalIQ.utils.tooltip.addPopover(me.ui.title, titleTooltip);
            }

            const primaryLabel = model.get('primaryLabel');
            let displayedPrimaryLabel = primaryLabel;
            const primaryLabelColor = model.get('primaryLabelColor');
            const primaryLabelFormatter = model.get('primaryLabelFormatter');

            if (_.isFunction(primaryLabelFormatter)) {
                me.ui.primaryLabel.text(primaryLabelFormatter(displayedPrimaryLabel));
            } else {
                if (_.isString(primaryLabelFormatter)) {
                    displayedPrimaryLabel = d3.format(primaryLabelFormatter)(primaryLabel);
                }

                me.ui.primaryLabel.text(displayedPrimaryLabel);
            }

            if (primaryLabel < 0) {
                me.$el.addClass(me.negativeClassName);
            }

            if (typeof primaryLabelColor === 'function') {
                me.ui.primaryLabel.css('color', primaryLabelColor(primaryLabel));
            } else if (primaryLabelColor) {
                me.ui.primaryLabel.css('color', primaryLabelColor);
            }

            const secondaryLabel = model.get('secondaryLabel');
            const secondaryLabelColor = model.get('secondaryLabelColor');
            const secondaryLabelFormatter = model.get('secondaryLabelFormatter');

            if (secondaryLabel !== null && secondaryLabel !== void(0)) {
                if (typeof secondaryLabelFormatter === 'function') {
                    me.ui.secondaryLabel.text(secondaryLabelFormatter(secondaryLabel));
                } else {
                    me.ui.secondaryLabel.text(secondaryLabel);
                }

                if (typeof secondaryLabelColor === 'function') {
                    me.ui.secondaryLabel.css('color', secondaryLabelColor(secondaryLabel));
                } else if (secondaryLabelColor) {
                    me.ui.secondaryLabel.css('color', secondaryLabelColor);
                }
            }

            const tooltip = model.get('tooltip');
            if (tooltip) {
                RivalIQ.utils.tooltip.addPopover(me.ui.labels, tooltip, {
                    placement: (me.getOption('barIndex') === 0 ? 'top' : 'bottom')
                });
            }

            let widths;
            const options = me.options || {};
            const value = model.get('primaryLabel');

            if (options.max < 0) {
                // all negative
                widths = me.calculateRelativeWidths(value / options.min, me.allNegativeThreshold);
                me.ui.wrapper.prepend(me.createBumper(me.toPercent(widths.offset)));
                me.ui.bar.attr('data-percent', widths.bar);
            } else if (options.min >= 0) {
                // all positive, non-zero max (that means they are all zero) and non-zero value
                if (options.max > 0 && value > 0) {
                    widths = me.calculateRelativeWidths(value / options.max);
                    me.ui.bar.attr('data-percent', widths.bar);
                }
            } else {
                // some positive (including zero), some negative
                let max = options.max;
                const absoluteMin = Math.abs(options.min);

                // we need to compare the current positive maximum with
                // the absolute value of the negative value to determine
                // the true maximum (e.g., +1 vs -3, max 3)
                if (absoluteMin > max) {
                    max = options.min;
                }

                const threshold = value < 0 ? me.mixedNegativeThreshold : me.mixedPositiveThreshold;

                // because the max can actually be a min (negative) we need to
                // make sure that we take the absolute value of our percentage
                widths = me.calculateRelativeWidths(Math.abs(value / max), threshold);

                if (value >= 0) {
                    widths.offset = 0.5;
                }

                me.ui.wrapper.prepend(me.createBumper(me.toPercent(widths.offset)));

                me.ui.bar.attr('data-percent', widths.bar);
            }

            if (model.get('dataNotAvailable')) {
                me.ui.notAvailable.css('display', 'inline-block');
                me.ui.wrapper.hide();

                const notAvailableTooltip = model.get('notAvailableTooltip');
                if (notAvailableTooltip) {
                    RivalIQ.utils.tooltip.addPopover(me.ui.notAvailable, notAvailableTooltip);
                }
            } else if (model.get('hasPartialData')) {
                me.ui.partialDataWarning.css('display', 'inline-block');
                const partialDataTooltip = model.get('partialDataTooltip');
                if (partialDataTooltip) {
                    RivalIQ.utils.tooltip.addPopover(me.ui.partialDataWarning, partialDataTooltip);
                }
            }
        },
        toPercent: function (value) {
            return d3.format('p')(value);
        },
        calculateRelativeWidths: function (percent, threshold) {
            let p = percent;

            if (threshold) {
                p *= threshold;
            }

            const difference = (p < 0) ? threshold + p : threshold - p;
            const absoluteDifference = Math.abs(difference);

            return {
                bar: p,
                offset: absoluteDifference
            };
        },
        createBumper: function (percent) {
            return $('<div />').attr('data-percent', percent.replace('%', '') / 100);
        },

        serializeData: function () {
            const me = this;

            return {
                notAvailableMessage: me.model.get('notAvailableMessage') || 'Not available',
                partialDataMessage: me.model.get('partialDataMessage') || 'Data Incomplete'
            };
        }
    });

}, window.d3);
